import { useEffect } from "react";
import { Link } from "react-router";
import { useSnapshot } from "valtio";
import { adminStore, fetchDashboardStats } from "../stores/admin";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Users,
  AppWindow,
  Shield,
  Building2,
  Activity,
  TrendingUp,
  AlertCircle,
} from "lucide-react";

interface StatCardProps {
  title: string;
  value: number;
  icon: React.ReactNode;
  href: string;
  loading: boolean;
}

function StatCard({ title, value, icon, href, loading }: StatCardProps) {
  return (
    <Link to={href}>
      <Card className="hover:bg-accent transition-colors cursor-pointer">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">{title}</CardTitle>
          {icon}
        </CardHeader>
        <CardContent>
          {loading ? (
            <Skeleton className="h-8 w-16" />
          ) : (
            <div className="text-2xl font-bold">{value}</div>
          )}
        </CardContent>
      </Card>
    </Link>
  );
}

export default function AdminDashboard() {
  const { stats, loading, error } = useSnapshot(adminStore);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const statCards = [
    {
      title: "Users",
      value: stats.usersCount,
      icon: <Users className="h-4 w-4 text-muted-foreground" />,
      href: "/users",
    },
    {
      title: "Apps",
      value: stats.appsCount,
      icon: <AppWindow className="h-4 w-4 text-muted-foreground" />,
      href: "/apps",
    },
    {
      title: "Roles",
      value: stats.rolesCount,
      icon: <Shield className="h-4 w-4 text-muted-foreground" />,
      href: "/roles",
    },
    {
      title: "Organizations",
      value: stats.orgsCount,
      icon: <Building2 className="h-4 w-4 text-muted-foreground" />,
      href: "/orgs",
    },
  ];

  return (
    <div className="flex h-full flex-col">
      {/* Fixed Header */}
      <header className="shrink-0 border-b bg-background px-4 py-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <h1 className="text-3xl font-bold">Admin Dashboard</h1>
          <p className="mt-2 text-muted-foreground">
            Manage users, apps, roles, and organizations
          </p>
        </div>
      </header>

      {/* Scrollable Content */}
      <main className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
          {/* Error State */}
          {error && (
            <Alert variant="destructive" className="mb-6">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Stats Grid */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {statCards.map((stat) => (
              <StatCard key={stat.href} {...stat} loading={loading} />
            ))}
          </div>

          {/* Quick Actions & System Info */}
          <div className="grid gap-4 md:grid-cols-2 mt-8">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="h-5 w-5" />
                  Quick Actions
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Link
                  to="/users"
                  className="block p-3 rounded-lg hover:bg-accent transition-colors"
                >
                  <div className="font-medium">Manage Users</div>
                  <div className="text-sm text-muted-foreground">
                    View and manage user accounts
                  </div>
                </Link>
                <Link
                  to="/apps"
                  className="block p-3 rounded-lg hover:bg-accent transition-colors"
                >
                  <div className="font-medium">Manage Apps</div>
                  <div className="text-sm text-muted-foreground">
                    Configure OAuth applications
                  </div>
                </Link>
                <Link
                  to="/roles"
                  className="block p-3 rounded-lg hover:bg-accent transition-colors"
                >
                  <div className="font-medium">Manage Roles</div>
                  <div className="text-sm text-muted-foreground">
                    Configure access control roles
                  </div>
                </Link>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5" />
                  System Info
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Total Users</span>
                    <span className="font-medium">
                      {loading ? (
                        <Skeleton className="h-4 w-12 inline-block" />
                      ) : (
                        stats.usersCount
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Active Apps</span>
                    <span className="font-medium">
                      {loading ? (
                        <Skeleton className="h-4 w-12 inline-block" />
                      ) : (
                        stats.appsCount
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      Configured Roles
                    </span>
                    <span className="font-medium">
                      {loading ? (
                        <Skeleton className="h-4 w-12 inline-block" />
                      ) : (
                        stats.rolesCount
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Organizations</span>
                    <span className="font-medium">
                      {loading ? (
                        <Skeleton className="h-4 w-12 inline-block" />
                      ) : (
                        stats.orgsCount
                      )}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
